import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';

declare global {
	interface Window {
		dataLayer: any[];
		gtag: (...args: any[]) => void;
	}
}

@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {
	private platformId = inject(PLATFORM_ID);
	private measurementId: string;
	private isInitialized = false;

	constructor() {
		this.measurementId = environment.firebase.measurementId;
		this.initializeGtag();
	}

	/**
	 * Initialize gtag.js script and configuration
	 * Note: dataLayer and gtag function are initialized in index.html
	 * This method loads the gtag.js script with the correct measurement ID from environment
	 */
	private initializeGtag(): void {
		if (!isPlatformBrowser(this.platformId)) {
			return;
		}

		// Ensure dataLayer and gtag are initialized (they should be from index.html)
		if (!window.dataLayer) {
			window.dataLayer = window.dataLayer || [];
		}
		if (!window.gtag) {
			window.gtag = function(...args: any[]) {
				window.dataLayer.push(args);
			};
		}

		// Load gtag.js script with the correct measurement ID from environment
		const script = document.createElement('script');
		script.async = true;
		script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
		
		// Configure GA4 after script loads
		script.onload = () => {
			window.gtag('config', this.measurementId, {
				send_page_view: false
			});
			this.isInitialized = true;
		};
		
		// Append to head to load as early as possible
		document.head.appendChild(script);
	}

	/**
	 * Set user ID for tracking and user properties
	 */
	setUserId(userId: string | null): void {
		if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
			return;
		}

		if (userId) {
			// Set user_id in config
			window.gtag('config', this.measurementId, {
				user_id: userId
			});
			
			// Set user property for firebase_id
			window.gtag('set', 'user_properties', {
				firebase_id: userId
			});
		} else {
			// Clear user_id and user properties when user signs out
			window.gtag('config', this.measurementId, {
				user_id: null
			});
			window.gtag('set', 'user_properties', {
				firebase_id: null
			});
		}
	}

	/**
	 * Track a page view
	 */
	trackPageView(pagePath: string, pageTitle?: string): void {
		if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
			return;
		}

		window.gtag('event', 'page_view', {
			page_path: pagePath,
			page_title: pageTitle || pagePath
		});
	}

	/**
	 * Track a custom event
	 */
	trackEvent(eventName: string, eventParams?: Record<string, any>): void {
		if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
			return;
		}

		window.gtag('event', eventName, eventParams);
	}

	/**
	 * Track user sign up
	 * Note: firebase_id is automatically included via user_properties set in setUserId()
	 */
	trackSignUp(): void {
		this.trackEvent('sign_up');
	}

	/**
	 * Track user sign in
	 * Note: firebase_id is automatically included via user_properties set in setUserId()
	 */
	trackSignIn(): void {
		this.trackEvent('sign_in');
	}

	/**
	 * Track user sign out
	 * Note: firebase_id is automatically included via user_properties set in setUserId()
	 */
	trackSignOut(): void {
		this.trackEvent('sign_out');
	}

	/**
	 * Track game start
	 */
	trackGameStart(): void {
		this.trackEvent('game_start');
	}

	/**
	 * Track game complete
	 */
	trackGameComplete(): void {
		this.trackEvent('game_complete');
	}

	/**
	 * Track decision made
	 */
	trackDecisionMade(choice: string): void {
		this.trackEvent('decision_made', {
			choice: choice
		});
	}

	/**
	 * Track prize won
	 */
	trackPrizeWon(prizeId?: string, prizeName?: string): void {
		const eventParams: Record<string, any> = {};
		if (prizeId) {
			eventParams['prize_id'] = prizeId;
		}
		if (prizeName) {
			eventParams['prize_name'] = prizeName;
		}
		this.trackEvent('prize_won', eventParams);
	}
}

